import { Avatar, AvatarFallback } from "@components/ui/avatar";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@components/ui/sidebar";
import { EllipsisVerticalIcon, LogOutIcon, Users2Icon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { ROUTES } from "@/routes_new";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/hooks/useUser";
import { getOrganizations } from "@/api/endpoints/organization";
import { useOrganization } from "@/hooks/useOrganization";
import { UserActions } from "./UserActions";
import { OrganizationSwitcher } from "./OrganizationSwitcher";
import { StaffActions } from "./StaffActions";
import { Skeleton } from "@components/ui/skeleton";

export function UserDropdown({
  orgUuid,
  className,
}: {
  orgUuid: string;
  className?: string;
}) {
  const { isMobile } = useSidebar();

  // Server state
  const { data: user, isLoading: isUserLoading } = useUser();

  const userId = user?.ok ? user.data.id : null;
  const isStaff = user?.ok ? user.data.is_staff : false;
  const defaultOrganization = user?.ok ? user.data.default_organization : null;

  const { data: organizations, isLoading: isOrganizationsLoading } = useQuery({
    queryKey: [userId, "organizations"],
    queryFn: () => getOrganizations(),
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  const { data: organization } = useOrganization();

  if (!user?.ok || isUserLoading || isOrganizationsLoading)
    return <Skeleton className="h-12 w-full" />;

  // Helpers
  const displayName = user?.ok ? user.data.name || user.data.email : "";
  const displaySubtext =
    organization?.ok && organization.data?.name
      ? organization.data.name
      : user?.ok
        ? user.data.email
        : "";

  const hasMultipleOrgs = organizations?.ok
    ? organizations.data.length > 1
    : false;

  return (
    <SidebarMenuItem className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid="toggle-organization-menu">
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar className="h-8 w-8 rounded-lg grayscale">
              <AvatarFallback className="rounded-lg">
                {displayName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{displayName}</span>
              <span className="text-muted-foreground truncate text-xs">
                {displaySubtext}
              </span>
            </div>
            <EllipsisVerticalIcon className="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg popover-menu"
          side={isMobile ? "top" : "right"}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg">
                  {displayName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {displaySubtext}
                </span>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <UserActions orgUuid={orgUuid} />

            {hasMultipleOrgs && (
              <>
                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger id="organization-picker-select">
                    <Users2Icon />
                    <Trans>Byt konto</Trans>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="p-0 w-96">
                    <OrganizationSwitcher
                      organizations={
                        organizations?.ok ? organizations.data : []
                      }
                      orgUuid={orgUuid}
                    />
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </>
            )}

            {isStaff && (
              <StaffActions
                organization={organization?.ok ? organization.data : null}
                defaultOrganization={defaultOrganization}
                orgUuid={orgUuid}
              />
            )}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuItem variant="destructive" asChild>
            <a href={ROUTES.LOGOUT}>
              <LogOutIcon />
              <Trans>Logga ut</Trans>
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
